<template>
  <div class="aspect-[0.9]">
    <ChoroplethChart :data="mapData" :options="mapOptions" />
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { createTypedChart } from 'vue-chartjs'
import { Chart as ChartJS, Tooltip } from 'chart.js'
import {
  ChoroplethController,
  ColorScale,
  GeoFeature,
  ProjectionScale,
  topojson,
} from 'chartjs-chart-geo'

import provincesTopologyRaw from '../../utils/nl_provinces.topojson?raw'
import { computed } from 'vue'

const props = defineProps({
  dataset: {
    type: Object,
    default: () => ({}),
  },
  options: {
    type: Object,
    default: () => ({}),
  },
})

ChartJS.register(GeoFeature, ProjectionScale, ColorScale, Tooltip)

const provincesTopology = JSON.parse(provincesTopologyRaw)
const provinces = topojson.feature(
  provincesTopology,
  provincesTopology.objects.provincie_2026,
).features

const mapData = ref({
  labels: provinces.map((d) => d.properties.statnaam),
  datasets: [
    {
      label: 'Provinces',
      outline: provinces,
      data: [],
    },
  ],
})

const mapOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  ...props.options,
  scales: {
    projection: {
      axis: 'x',
      projection: 'mercator',
      ...props.options.scales?.projection,
    },
    color: {
      axis: 'x',
      quantize: 10,
      legend: {
        position: 'bottom-right',
        align: 'right',
      },
      ...props.options.scales?.color,
    },
  },
  plugins: {
    datalabels: false,
    ...props.options.plugins,
    legend: {
      display: false,
      ...props.options.plugins?.legend,
    },
    tooltip: {
      callbacks: {
        title(context) {
          return context[0].raw.feature.properties.statnaam
        },
        label(context) {
          const value = context.raw.value || 0
          let total = 0
          for (const key in props.dataset?.data) {
            total += props.dataset.data[key]
          }
          const percentage = total ? ((value / total) * 100).toFixed(1) : 0
          return `${context.dataset.label}: ${value} (${percentage}%)`
        },
      },
      ...props.options.plugins?.tooltip,
    },
  },
}))

watch(
  () => props.dataset,
  (dataset) => {
    if (dataset?.data) {
      mapData.value = {
        labels: provinces.map((d) => d.properties.statnaam),
        datasets: [
          {
            label: dataset.label,
            outline: provinces,
            data: provinces.map((d) => ({
              feature: d,
              value: dataset.data[d.properties.statnaam.toLowerCase()] || 0,
            })),
          },
        ],
      }
    }
  },
  { immediate: true },
)

const ChoroplethChart = createTypedChart('choropleth', ChoroplethController)
</script>
